'use client';

import { usePulseTracker } from '@cineamo/pulse/react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { SeatDto, SeatType } from '@cineamo/api/models/seat/SeatDto.types';
import { SeatCategoryDto } from '@cineamo/api/models/seat-category/SeatCategoryDto.types';
import { WebsiteInputStyle, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { toBackendLocale } from '@cineamo/ui/src/constants/locale-constants';

import { apiResponseHandler } from '@cineamo/api/helpers/api-response-helper/api-response-helper';
import { setCartConcessions } from '@cineamo/api/resources/cart-concession/cart-concession-api';
import { PaymentApiSessionResponse } from '@cineamo/api/resources/payment-api/CineamoPaymentApiResponse.types';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { getLocaleFromLanguage } from '@cineamo/ui/src/helpers/locale-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { useConcessionCatalog } from '@cineamo/composables/src/hooks/useConcessionCatalog';
import { useMovie } from '@cineamo/composables/src/hooks/useMovie';
import useSessionStorage from '@cineamo/composables/src/hooks/useSessionStorage';
import useStripeUrlQueryParams from '@cineamo/composables/src/hooks/useStripeUrlQueryParams';
import { useIsBelowLg } from '@cineamo/ui/src/hooks/useMediaQuery';

import { IconBack16 } from '@cineamo/ui/src/icons/ic-back';
import { IconPlus16 } from '@cineamo/ui/src/icons/ic-plus';
import { screenFilmFromMovie } from '@cineamo/ui/src/room-3d/screen-film-from-movie';
import WebsiteHeader from '@cineamo/ui/src/website/WebsiteHeader';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { BookingStep, CartUuidStorageKey, useCartProvider } from '@cineamo/composables/src/providers/cartProvider';
import useUserStore from '@cineamo/composables/src/stores/useUserStore';
import CheckoutButton from '@cineamo/composables/src/ticket-booking/CheckoutButton';
import { CheckoutShowingInfoContext } from '@cineamo/composables/src/ticket-booking/showing-info-context';
import ShowingDetails from '@cineamo/composables/src/ticket-booking/ShowingDetails';

import { useSeatMap } from '../hooks/useSeatMap';

import { BuyerDetails, createPaymentIntent, syncSeatLocks } from './actions';
import BookingFooter from './BookingFooter';
import CartSummary from './CartSummary';
import CheckoutForm, { storeBuyer } from './CheckoutForm';
import { isConcessionsWithoutTicketsError } from './concession-helper';
import LockCountdown from './countdown/LockCountdown';
import type { Wallet } from './ExpressCheckout';
import GiftCardRedemption from './GiftCardRedemption';
import PaymentFailedView from './PaymentFailedView';
import PaymentSucceededView from './PaymentSucceededView';
import { legendFurnitureOf } from './SeatMapLegend';
import SeatSelection from './SeatSelection';
import SnackSelectionModal from './SnackSelectionModal';
import { useErrorHandler } from './useErrorHandler';

const TicketBookingPage: React.FC<{
    seatCategories: SeatCategoryDto[];
    showCinemaName?: boolean;
    websiteStyle: WebsiteStyle & WebsiteInputStyle;
    sidebarStyle: string;
    contentMaxHeight: string;
    /** The page's header has a back arrow on small screens that steps back through the checkout, so no step needs a link of its own there. */
    headerHasBackButton?: boolean;
    /**
     * The app's showing info, opened by "Details" on small screens; it reads its open state with
     * `useCheckoutShowingInfo`. Left out, "Details" links to the film's page.
     */
    showingInfoModal?: React.ReactNode;
    /** The cinema sells concessions online (`cinemaConfiguration.isConcessionSaleEnabled`). */
    isConcessionSaleEnabled?: boolean;
    /** Shows the gift card field in the cart step; off by default, as the cinema has to have redemption enabled. */
    isGiftCardRedemptionEnabled?: boolean;
    /** Offers "Find best seats" over the seat map; on by default, the cinema can switch it off. */
    isBestSeatsEnabled?: boolean;
}> = ({
    seatCategories,
    showCinemaName,
    websiteStyle,
    sidebarStyle,
    contentMaxHeight,
    headerHasBackButton,
    showingInfoModal,
    isConcessionSaleEnabled = false,
    isGiftCardRedemptionEnabled,
    isBestSeatsEnabled = true
}) => {
    const {
        selectedSeats,
        setItems,
        currency,
        cartUuid,
        paidCartUuid,
        setPaidCartUuid,
        showing,
        items,
        isProcessing,
        total,
        concessions,
        setConcessions,
        refreshCartDto,
        reloadCart,
        unselectSeat,
        amountDue,
        currentStep,
        setCurrentStep
    } = useCartProvider();
    const { handleErrorResponse } = useErrorHandler();
    const { user } = useUserStore();
    const locale = useLocale();
    const [isPageHaveUnsavedChanges, setIsPageHaveUnsavedChanges] = useSessionStorage(
        'isPageHaveUnsavedChanges',
        'false'
    );

    const { seats, room, roomObjects, isLoading: isSeatMapLoading } = useSeatMap(showing?.cinemaRoomId);
    const { movie } = useMovie(showing?._embedded?.content?.movieId, locale, showing?.cinemaId);
    const screenFilm = useMemo(() => (movie ? (screenFilmFromMovie(movie) ?? undefined) : undefined), [movie]);
    const { track } = useAnalytics();
    const { track: trackPulse } = usePulseTracker();

    const seatsInCart = useMemo(() => items.filter((item) => item._embedded?.product?.type === 'SeatLock'), [items]);
    const { paymentIntent, paymentIntentClientSecret, redirectStatus } = useStripeUrlQueryParams();

    const t = useTranslations('tickets');
    const { tabs: concessionTabs } = useConcessionCatalog(showing?.cinemaId ?? 0, isConcessionSaleEnabled);

    useEffect(() => {
        // Snacks are only sold together with tickets (`concessions_without_tickets`).
        if (selectedSeats.length > 0 || concessions.length === 0 || !cartUuid) return;
        void setCartConcessions(
            cartUuid,
            concessions.flatMap(({ cinemaProductId }) =>
                cinemaProductId === null ? [] : [{ cinemaProductId, quantity: 0 }]
            )
        ).then(() => refreshCartDto());
        setConcessions([]);
    }, [selectedSeats.length, concessions, cartUuid, setConcessions, refreshCartDto]);

    const [snackSelectionModalIsOpen, setSnackSelectionModalIsOpen] = useState(false);
    const [isShowingInfoOpen, setIsShowingInfoOpen] = useState(false);
    const showingInfoState = useMemo(
        () => ({ isOpen: isShowingInfoOpen, close: () => setIsShowingInfoOpen(false) }),
        [isShowingInfoOpen]
    );
    const [isCheckoutButtonLoading, setIsCheckoutButtonLoading] = useState(false);
    const [paymentSubmitTrigger, setPaymentSubmitTrigger] = useState(0);
    // Why the guest is back on the checkout after a payment redirect; cleared by the next attempt.
    const [paymentNotice, setPaymentNotice] = useState<string>();
    // Set when the cart drops a seat by its x: the step stays, the locks are already synced.
    const keepStepOnSeatChange = useRef(false);
    // The checkout's payment method ('wallet' or a Payment Element type); it decides the form and the footer's button.
    const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
    const [wallet, setWallet] = useState<Wallet | null>(null);
    const isBelowLg = useIsBelowLg();

    /**
     * Creates the PaymentIntent for the buyer: its client secret, 'paid' when nothing is due (a free showing, or gift
     * cards cover everything: the backend has already completed the order, CIN-15034), or null after an error it has
     * shown. A refused address goes to `onEmailRefused`, else to a toast.
     */
    const requestPaymentIntent = useCallback(
        async (buyer: BuyerDetails, onEmailRefused?: () => void): Promise<string | 'paid' | null> => {
            try {
                const res = await createPaymentIntent(
                    cartUuid,
                    buyer,
                    currency,
                    // An /en-US/ page would otherwise send en_US, which the API rejects (CIN-15225).
                    toBackendLocale(getLocaleFromLanguage(locale))
                );
                let result: string | 'paid' | null = null;
                apiResponseHandler<PaymentApiSessionResponse>(
                    res,
                    (successResponse) => {
                        result = 'client_secret' in successResponse ? successResponse.client_secret : 'paid';
                    },
                    (errorResponse) => {
                        // The address itself was refused, e.g. a domain that cannot receive mail: say so
                        // beside the field, where the buyer can fix it (CIN-15349).
                        if ('receiptEmail' in (errorResponse.data?.parameters ?? {})) {
                            if (onEmailRefused) {
                                onEmailRefused();
                                return;
                            }
                            showToast({
                                toastType: toast.info,
                                messageTitle: t('tickets.payment.email-input.email-undeliverable')
                            });
                            return;
                        }
                        showToast({
                            toastType: toast.info,
                            // `detail` carries the reason ("Bitte wählen Sie die Plätze erneut aus. (C5200)");
                            // `title` is the generic problem type ("Invalid parameter") and says nothing.
                            messageTitle: isConcessionsWithoutTicketsError(errorResponse)
                                ? t('error.concessions-without-tickets')
                                : errorResponse.data?.detail ||
                                  errorResponse.data?.title ||
                                  t('error.something-went-wrong')
                        });
                    }
                );
                return result;
            } catch {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('error.something-went-wrong')
                });
                return null;
            }
        },
        [cartUuid, currency, locale, t]
    );

    const completePayment = useCallback(() => {
        void reloadCart();
        setPaidCartUuid(cartUuid);
        setCurrentStep('03_payment_complete');
    }, [cartUuid, reloadCart, setCurrentStep, setPaidCartUuid]);

    const onCheckoutButtonClick = useCallback(async () => {
        switch (currentStep) {
            case '01_seat_picking':
                {
                    setIsCheckoutButtonLoading(true);
                    const result = await syncSeatLocks(cartUuid, showing.id, selectedSeats, items);
                    apiResponseHandler(
                        result?.response ?? null,
                        async () => {
                            const cartItems = result.cartItems;
                            setItems(cartItems);
                            // The totals of step 01.5 and the payment are the API cart's, so read them once synced.
                            await refreshCartDto();
                            setCurrentStep('01.5_cart');
                        },
                        (errorResponse) => {
                            handleErrorResponse(errorResponse);
                        }
                    );
                    setIsCheckoutButtonLoading(false);
                }
                break;
            case '01.5_cart':
                // Each visit starts on the wallet, where there is one: express first.
                setSelectedMethod(wallet ? 'wallet' : null);
                setCurrentStep('02_payment');
                break;
            case '02_payment':
                setPaymentNotice(undefined);
                setPaymentSubmitTrigger((trigger) => trigger + 1);
                break;
        }
    }, [
        cartUuid,
        currentStep,
        handleErrorResponse,
        items,
        refreshCartDto,
        selectedSeats,
        setCurrentStep,
        setItems,
        showing.id
    ]);

    /**
     * A seat's x. On seat picking it only unselects; on the cart the locks are synced without it and the cart
     * stays, instead of the seat-count change sending the guest back to the map. The last seat goes back there.
     */
    const removeSeat = useCallback(
        async (seatId: SeatDto['id']) => {
            const remaining = selectedSeats.filter((seat) => seat.id !== seatId);
            if (currentStep !== '01.5_cart' || remaining.length === 0) {
                unselectSeat(seatId);
                return;
            }
            setIsCheckoutButtonLoading(true);
            const result = await syncSeatLocks(cartUuid, showing.id, remaining, items);
            apiResponseHandler(
                result?.response ?? null,
                async () => {
                    keepStepOnSeatChange.current = true;
                    setItems(result.cartItems);
                    unselectSeat(seatId);
                    await refreshCartDto();
                },
                (errorResponse) => handleErrorResponse(errorResponse)
            );
            setIsCheckoutButtonLoading(false);
        },
        [
            cartUuid,
            currentStep,
            handleErrorResponse,
            items,
            refreshCartDto,
            selectedSeats,
            setItems,
            showing.id,
            unselectSeat
        ]
    );

    useLayoutEffect(() => {
        if (cartUuid === paidCartUuid) {
            setCurrentStep('03_payment_complete');
        }
    }, [cartUuid, paidCartUuid, setCurrentStep]);

    useEffect(() => {
        // A failed redirect goes back to the checkout instead (see below).
        if (paymentIntentClientSecret && redirectStatus !== 'failed') {
            setCurrentStep('03_payment_complete');
        }
    }, [paymentIntentClientSecret, redirectStatus, setCurrentStep]);

    useEffect(() => {
        // Back from a payment redirect the cart loads its seats late; that must not undo the result step below.
        if (redirectStatus) return;
        if (keepStepOnSeatChange.current) {
            keepStepOnSeatChange.current = false;
            return;
        }
        setCurrentStep('01_seat_picking');
    }, [selectedSeats.length, setCurrentStep, redirectStatus]);

    const previousStep = useRef<BookingStep>(currentStep);
    useEffect(() => {
        if (previousStep.current !== currentStep) {
            track('Step Changed', {
                previousStep: previousStep.current,
                currentStep: currentStep,
                cartTotal: total.toString(),
                cartItemsCount: items.length
            });
            previousStep.current = currentStep;
        }

        // One value, set once: setting it twice in one run (seat picking with no seat chosen while the cart still
        // holds seat locks) flipped it between renders, and since the setter changes with the value the effect
        // looped. The rules are the old ones, the later one winning.
        let hasUnsavedChanges = currentStep === '01_seat_picking' ? selectedSeats.length > 0 : undefined;
        if (selectedSeats.length !== seatsInCart.length) hasUnsavedChanges = true;
        if (currentStep === '01.5_cart' || currentStep === '02_payment') hasUnsavedChanges = true;
        if (currentStep === '03_payment_complete' || currentStep === '03_payment_error') hasUnsavedChanges = false;
        if (hasUnsavedChanges !== undefined) {
            setIsPageHaveUnsavedChanges(hasUnsavedChanges ? 'true' : 'false');
        }
    }, [
        currentStep,
        items.length,
        seatsInCart.length,
        selectedSeats.length,
        setIsPageHaveUnsavedChanges,
        total,
        track
    ]);

    useEffect(() => {
        // Wero (and other bank-backed methods) come back `pending` while the bank confirms; the success view follows
        // the order until it is paid or cancelled, so pending goes there too instead of back to the seat map.
        if (redirectStatus === 'succeeded' || redirectStatus === 'pending') {
            // The success view reads this cart's order; until the webhook marks it paid, nothing else points there.
            // Only the first cart seen for this payment counts: once the provider finds it paid it swaps in a fresh,
            // empty cart while `redirect_status` stays in the URL, also across a reload (CIN-15388).
            if (cartUuid && paymentIntent) {
                const redirectedCartKey = `${CartUuidStorageKey}-redirect-${paymentIntent}`;
                let redirectedCartUuid = cartUuid;
                try {
                    redirectedCartUuid = sessionStorage.getItem(redirectedCartKey) ?? cartUuid;
                    sessionStorage.setItem(redirectedCartKey, redirectedCartUuid);
                } catch {
                    // Storage blocked: fall back to the current cart.
                }
                setPaidCartUuid(redirectedCartUuid);
            }
            storeBuyer(null);
            setCurrentStep('03_payment_complete');
        }
        // Cancelled at PayPal, refused by the bank...: the seats are still held, so back to the checkout to try
        // again or pick another method, not to a dead end that throws the seats away. Once the cart has loaded them;
        // with none left (the hold ran out) the guest picks seats again.
        if (redirectStatus === 'failed' && selectedSeats.length > 0) {
            setPaymentNotice(t('checkout.payment-failed-retry'));
            setCurrentStep('02_payment');
        }
    }, [redirectStatus, paymentIntent, cartUuid, selectedSeats.length, setCurrentStep, setPaidCartUuid, t]);

    // Pulse ticket_purchase on confirmed payment only: the Stripe redirect back with
    // `redirect_status=succeeded`, or an in-session paid cart. Reaching the
    // 03_payment_complete step alone is not enough — it is also entered while the payment
    // intent is still open.
    const pulsePurchaseTrackedRef = useRef(false);
    useEffect(() => {
        const isConfirmedPurchase = redirectStatus === 'succeeded' || (!!cartUuid && cartUuid === paidCartUuid);
        if (!isConfirmedPurchase || pulsePurchaseTrackedRef.current || !showing?.movieId) return;
        pulsePurchaseTrackedRef.current = true;
        trackPulse({
            movie_id: showing.movieId,
            event_type: 'ticket_purchase',
            context: {
                showing_id: showing.id,
                // Not paidCartUuid: on the redirect it still holds the previous order until the effect above lands.
                cart_uuid: cartUuid
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectStatus, cartUuid, paidCartUuid]);

    const showingDetails = useMemo(() => {
        return (
            <>
                <ShowingDetails
                    showing={showing}
                    movie={movie ?? undefined}
                    showCinemaName={showCinemaName}
                    cinemaRoom={room?.dto ?? undefined}
                />
                <LockCountdown />
            </>
        );
    }, [showing, movie, showCinemaName, room?.dto]);

    const onPageUnload = useCallback((e: BeforeUnloadEvent) => {
        // Read at the event: the header's back arrow clears it once the guest confirmed leaving in its modal.
        if (sessionStorage.getItem('isPageHaveUnsavedChanges') === 'true') {
            e.preventDefault();
        }
    }, []);

    useEffect(() => {
        if (isPageHaveUnsavedChanges) {
            window.addEventListener('beforeunload', onPageUnload);
        }
        if (!isPageHaveUnsavedChanges) {
            window.removeEventListener('beforeunload', onPageUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', onPageUnload);
        };
    }, [onPageUnload, isPageHaveUnsavedChanges]);

    if (!showing) {
        return (
            <CenterLayout className="mt-32">
                <WebsiteHeader title="Showing not found!" />
            </CenterLayout>
        );
    }

    // One instance: its Stripe Elements must not mount twice. Wide screens show it beside the cart, small ones below.
    const checkoutForm = (
        <CheckoutForm
            amount={amountDue}
            currency={currency}
            websiteStyle={websiteStyle}
            submitTrigger={paymentSubmitTrigger}
            defaultBuyer={{ email: user?.email, firstName: user?.firstName, lastName: user?.lastName }}
            requestPaymentIntent={requestPaymentIntent}
            onPaymentComplete={completePayment}
            selectedMethod={selectedMethod}
            onSelectMethod={setSelectedMethod}
            notice={paymentNotice}
        />
    );

    let currentStepView: React.JSX.Element | null;
    switch (currentStep) {
        case '01_seat_picking':
            currentStepView = (
                <SeatSelection
                    className="w-full"
                    isLoading={isSeatMapLoading}
                    room={room}
                    seats={seats}
                    roomObjects={roomObjects}
                    seatCategories={seatCategories}
                    selectedSeatColor={websiteStyle.primaryColor}
                    screenFilm={screenFilm}
                    isBestSeatsEnabled={isBestSeatsEnabled}
                />
            );
            break;
        case '01.5_cart':
            currentStepView = (
                <SeatSelection
                    className="hidden lg:block w-full"
                    room={room}
                    seats={seats}
                    roomObjects={roomObjects}
                    seatCategories={seatCategories}
                    selectedSeatColor={websiteStyle.primaryColor}
                    screenFilm={screenFilm}
                    isBestSeatsEnabled={isBestSeatsEnabled}
                />
            );
            break;
        case '02_payment':
            // Small screens put the form in the sidebar's column, below the film; see `checkoutForm`.
            currentStepView = isBelowLg ? null : <div className="lg:h-full lg:overflow-y-auto">{checkoutForm}</div>;
            break;
        case '03_payment_complete':
            currentStepView = <PaymentSucceededView />;
            break;
        case '03_payment_error':
            currentStepView = <PaymentFailedView />;
            break;
    }

    let backButton: React.JSX.Element | undefined;
    switch (currentStep) {
        case '01_seat_picking':
            backButton = (
                <Link
                    href={`/program/${showing._embedded?.content?.id}`}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('other-screenings')}
                </Link>
            );
            break;
        case '01.5_cart':
            backButton = (
                <Link
                    href=""
                    onClick={() => setCurrentStep('01_seat_picking')}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('back-to-seat-selection')}
                </Link>
            );
            break;
        case '02_payment':
            backButton = (
                <Link
                    href=""
                    onClick={() => setCurrentStep('01.5_cart')}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('back-to-shopping-cart')}
                </Link>
            );
            break;
        case '03_payment_complete':
        case '03_payment_error':
            break;
    }

    const isSidebarVisible = !['03_payment_complete', '03_payment_error'].includes(currentStep);
    const hasTables = roomObjects.some((roomObject) => roomObject.type === 'table');
    const hasWheelchairSeats = seats.some((seat) => [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(seat.type));
    const furniture = legendFurnitureOf(seats);

    return (
        <div className="relative flex flex-col lg:grow items-stretch">
            <div className="flex flex-col lg:flex-row lg:grow items-stretch">
                {snackSelectionModalIsOpen && (
                    <SnackSelectionModal
                        modalIsOpen={snackSelectionModalIsOpen}
                        setModalIsOpen={(value) => setSnackSelectionModalIsOpen(value)}
                        onClose={() => setSnackSelectionModalIsOpen(false)}
                        tabs={concessionTabs}
                    />
                )}
                {showingInfoModal && (
                    <CheckoutShowingInfoContext.Provider value={showingInfoState}>
                        {showingInfoModal}
                    </CheckoutShowingInfoContext.Provider>
                )}
                <div
                    className={cn(
                        'grow flex flex-col space-y-[12px] overflow-y-auto lg:overflow-y-hidden text-[var(--on-background-color)]',
                        contentMaxHeight,
                        sidebarStyle
                    )}>
                    {backButton && (
                        <div
                            className={cn(
                                'lg:py-4 ml-[9px] lg:ml-32 shrink-0',
                                headerHasBackButton &&
                                    ['01_seat_picking', '01.5_cart', '02_payment'].includes(currentStep) &&
                                    'hidden lg:block'
                            )}>
                            {backButton}
                        </div>
                    )}
                    <div className="grow flex flex-col min-h-0">
                        {isSidebarVisible && (
                            <div className="lg:hidden flex flex-col">
                                <ShowingDetails
                                    showing={showing}
                                    movie={movie ?? undefined}
                                    compactDetailsHref={`/program/${showing._embedded?.content?.id}`}
                                    onShowCompactDetails={
                                        showingInfoModal ? () => setIsShowingInfoOpen(true) : undefined
                                    }
                                />
                                <div className="flex flex-col mx-[9px]">
                                    <LockCountdown />
                                </div>
                            </div>
                        )}
                        <div className="w-full mx-auto lg:overflow-x-hidden space-y-16 lg:relative lg:h-0 lg:grow lg:min-h-0">
                            {currentStepView}
                        </div>
                        <div className="grow lg:hidden"></div>
                        <BookingFooter
                            seatCategories={seatCategories}
                            hasTables={hasTables}
                            hasWheelchairSeats={hasWheelchairSeats}
                            furniture={furniture}
                        />
                    </div>
                </div>

                {isSidebarVisible && (
                    <div
                        className={cn(
                            'relative lg:p-0 flex flex-col lg:w-[390px] lg:shrink-0 overflow-y-auto pb-[var(--booking-footer-height,169px)] lg:pb-0',
                            contentMaxHeight
                        )}>
                        <div className="hidden lg:flex flex-col">{showingDetails}</div>

                        {seats.length > 0 && (
                            // Small screens show the list in the footer's details while seats are picked and paid for.
                            <CartSummary
                                seats={seats}
                                onRemoveSeat={currentStep === '02_payment' ? undefined : removeSeat}
                                // 12px below the reservation banner, per Figma (Cinema Websites 503:89271).
                                className={currentStep === '01.5_cart' ? 'pt-12 lg:pt-0' : 'hidden lg:block'}
                            />
                        )}

                        {isGiftCardRedemptionEnabled && currentStep === '01.5_cart' && (
                            <GiftCardRedemption
                                className="mx-[9px] mt-16 lg:mx-16"
                                websiteStyle={websiteStyle}
                            />
                        )}

                        <div className="grow" />

                        {isConcessionSaleEnabled &&
                            concessionTabs.length > 0 &&
                            selectedSeats.length > 0 &&
                            currentStep === '01.5_cart' && (
                                <button
                                    type="button"
                                    onClick={() => setSnackSelectionModalIsOpen(true)}
                                    className={cn(
                                        'mx-[9px] lg:mx-16 my-16 flex min-h-48 items-center justify-center gap-8 rounded-4 border-2 px-16 text-button-16 outline-none',
                                        'border-[var(--primary-color)] text-[var(--primary-color)]',
                                        'focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2'
                                    )}>
                                    <IconPlus16 />
                                    {t('add-snacks')}
                                </button>
                            )}

                        {isBelowLg && currentStep === '02_payment' && checkoutForm}

                        <CheckoutButton
                            onCheckoutClick={onCheckoutButtonClick}
                            seats={seats}
                            seatCategories={seatCategories}
                            hasTables={hasTables}
                            hasWheelchairSeats={hasWheelchairSeats}
                            furniture={furniture}
                            isLoading={isCheckoutButtonLoading || isProcessing}
                            onRemoveSeat={currentStep === '01_seat_picking' ? removeSeat : undefined}
                            express={{
                                requestPaymentIntent,
                                onPaymentComplete: completePayment,
                                selectedMethod,
                                onWalletChange: (next) => {
                                    setWallet(next);
                                    // The wallet leads unless a method was chosen before it was found.
                                    if (next) setSelectedMethod((method) => method ?? 'wallet');
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TicketBookingPage;
